import { useState, useEffect, useMemo } from 'react';

// Custom hook for pagination with localStorage persistence
// Parameters:
//   data: array - the full data array to paginate
//   storageKey: string - key for localStorage to persist itemsPerPage
//   defaultItemsPerPage: number - default items per page (default: 25)
//
// Returns:
//   paginatedData: array - the sliced data for current page
//   currentPage: number - current page (1-indexed)
//   setCurrentPage: function - set current page
//   itemsPerPage: number - items per page
//   setItemsPerPage: function - set items per page (also persists to localStorage)
//   totalItems: number - total number of items
//   totalPages: number - total number of pages

function usePagination(data, storageKey, defaultItemsPerPage = 25) {
  // Initialize itemsPerPage from localStorage or default
  const [itemsPerPage, setItemsPerPageState] = useState(() => {
    let stored = null;
    try { stored = localStorage.getItem(`pagination_${storageKey}`); } catch (e) { /* private browsing */ }
    return stored ? parseInt(stored, 10) : defaultItemsPerPage;
  });

  const [currentPage, setCurrentPage] = useState(1);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(data.length / itemsPerPage);
  }, [data.length, itemsPerPage]);

  // Reset to page 1 when data changes significantly or itemsPerPage changes
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    } else if (totalPages === 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Wrapper for setItemsPerPage that also persists to localStorage
  const setItemsPerPage = (value) => {
    setItemsPerPageState(value);
    localStorage.setItem(`pagination_${storageKey}`, value.toString());
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Calculate the paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, itemsPerPage]);

  return {
    paginatedData,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalItems: data.length,
    totalPages
  };
}

export default usePagination;
